import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { ReviewStrip } from "@/components/review-strip";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import {
  FIRM,
  FIRM_ADDRESS,
  MICHAEL_PHONE,
  PENINSULA_ADDRESS,
  PENINSULA_OFFICE,
  faqSchema,
} from "@/lib/firm";
import {
  WILLS_LOCATIONS,
  WILL_FEES,
  getWillsLocation,
  type WillsLocation,
} from "@/lib/wills-locations";
import { PROBATE_LOCATIONS } from "@/lib/probate-locations";

const SITE = "https://coastsidelaw.com.au";

export const Route = createFileRoute("/wills/$suburb")({
  loader: ({ params }) => {
    const content = getWillsLocation(params.suburb);
    if (!content) throw notFound();
    return { content };
  },
  head: ({ params, loaderData }) => {
    const content = loaderData?.content;
    if (!content) {
      return { meta: [{ title: fitTitle("Not found") }, { name: "robots", content: "noindex" }] };
    }
    const url = `${SITE}/wills/${params.suburb}`;
    return {
      meta: [
        { title: fitTitle(content.metaTitle) },
        { name: "description", content: fitDescription(content.description) },
        {
          name: "keywords",
          content: `wills ${content.name}, will lawyer ${content.name}, estate lawyer ${content.name}, enduring power of attorney ${content.name}, estate planning ${content.name} QLD`,
        },
        { property: "og:title", content: fitTitle(content.metaTitle) },
        { property: "og:description", content: fitDescription(content.description) },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(faqSchema(content.faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LegalService",
            name: `${FIRM.name} — wills and estate lawyers, ${content.name}`,
            description: content.description,
            telephone: content.office === "peninsula" ? MICHAEL_PHONE : FIRM.phone,
            url,
            address:
              content.office === "peninsula"
                ? {
                    "@type": "PostalAddress",
                    streetAddress: PENINSULA_OFFICE.street,
                    addressLocality: PENINSULA_OFFICE.suburb,
                    addressRegion: PENINSULA_OFFICE.state,
                    postalCode: PENINSULA_OFFICE.postcode,
                    addressCountry: "AU",
                  }
                : {
                    "@type": "PostalAddress",
                    streetAddress: FIRM.street,
                    addressLocality: FIRM.suburb,
                    addressRegion: FIRM.state,
                    postalCode: FIRM.postcode,
                    addressCountry: "AU",
                  },
            areaServed: [content.name, ...content.nearby].map((n) => ({
              "@type": "Place",
              name: `${n}, Queensland`,
            })),
            serviceType: [
              "Wills",
              "Enduring powers of attorney",
              "Advance health directives",
              "Estate planning",
              "Probate and estate administration",
            ],
            offers: [
              {
                "@type": "Offer",
                name: "Simple will",
                priceSpecification: { "@type": "PriceSpecification", minPrice: Number(WILL_FEES.will.replace(/[^0-9.]/g, "")), priceCurrency: "AUD", valueAddedTaxIncluded: true },
              },
              {
                "@type": "Offer",
                name: "Will and enduring power of attorney pack",
                price: WILL_FEES.pack.replace(/[$,]/g, ""),
                priceCurrency: "AUD",
              },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              {
                "@type": "ListItem",
                position: 2,
                name: "Wills and estates",
                item: `${SITE}/wills-and-estates`,
              },
              { "@type": "ListItem", position: 3, name: content.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: WillsLocationPage,
});

const PRICES = [
  { t: "Simple will*", p: WILL_FEES.will, d: "Per person, incl. GST, free safe custody of the original" },
  { t: "Enduring power of attorney", p: WILL_FEES.epoa, d: "Per person, incl. GST, financial and personal/health matters" },
  { t: "Will and EPOA pack", p: WILL_FEES.pack, d: "Per person, incl. GST" },
  { t: "Couples pack", p: WILL_FEES.couples, d: "Two wills and two enduring powers of attorney, incl. GST" },
] as const;

function WillsLocationPage() {
  const { content } = Route.useLoaderData() as { content: WillsLocation };
  const probateHub = PROBATE_LOCATIONS.find((l) => l.slug === content.slug);

  return (
    <>
      <PageHero
        eyebrow={`Wills & estates — ${content.name}`}
        title={content.title}
        lead={content.lead}
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="mb-8 rounded-sm border-l-4 border-primary bg-secondary/50 p-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">Short answer</p>
          <p className="mt-2 text-base leading-relaxed text-foreground">
            {FIRM.name} prepares wills and enduring powers of attorney for {content.name} clients
            from our office at {content.office === "peninsula" ? PENINSULA_ADDRESS : FIRM_ADDRESS}.
            A simple will is a fixed {WILL_FEES.will} and a will and enduring power of attorney pack
            is {WILL_FEES.pack}, both including GST, with home visits available.
          </p>
        </div>
        <div className="rounded-sm border border-border bg-card p-7">
          <h2 className="font-serif text-2xl text-foreground">
            Fixed fees for {content.name} clients
          </h2>
          <dl className="mt-6 space-y-4 text-sm leading-relaxed">
            {PRICES.map((f) => (
              <div
                key={f.t}
                className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border/60 pb-4 last:border-0 last:pb-0"
              >
                <dt className="text-foreground">
                  {f.t}
                  <span className="block text-muted-foreground">{f.d}</span>
                </dt>
                <dd className="font-serif text-2xl text-primary">{f.p}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
            *Wills involving complex estate planning issues — blended families, companies, trusts or
            a self-managed super fund — are quoted separately, in writing, before we start.
          </p>
        </div>

        {content.paragraphs.map((p) => (
          <p key={p} className="mt-6 text-base leading-relaxed text-muted-foreground">
            {p}
          </p>
        ))}

        <h2 className="mt-14 font-serif text-3xl text-foreground">
          What we deal with for {content.name} families
        </h2>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          {content.points.map((c) => (
            <div key={c.t} className="rounded-sm border border-border bg-card p-6">
              <h3 className="font-serif text-lg text-foreground">{c.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.d}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Where we meet {content.name} clients</h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          {content.office === "peninsula" ? (
            <>
              Our peninsula office is at {PENINSULA_ADDRESS}, in postcode{" "}
              {content.postcodes.join(", ")} territory, and Michael Klein lives on the peninsula. We
              take will and enduring power of attorney instructions across {content.name},{" "}
              {content.nearby.join(", ")} — at the office, at home, in hospital or in a retirement
              village unit. Call {MICHAEL_PHONE} to book a time.
            </>
          ) : (
            <>
              Our Sunshine Coast office is at {FIRM_ADDRESS}, in postcode{" "}
              {content.postcodes.join(", ")} territory, and Erin Wilkinson lives at Golden Beach. We
              take will and enduring power of attorney instructions across {content.name},{" "}
              {content.nearby.join(", ")} — at the office, at home, in hospital or in a retirement
              village unit. Call {FIRM.phone} to book a time.
            </>
          )}
        </p>

        <PeopleStrip
          slugs={
            content.office === "peninsula"
              ? ["michael-klein", "erin-wilkinson"]
              : ["erin-wilkinson", "michael-klein"]
          } heading="Who will act for you" />

        <h2 className="mt-14 font-serif text-3xl text-foreground">Related pages</h2>
        <ul className="mt-5 space-y-2 text-sm">
          <li>
            <Link to="/wills-and-estates" className="font-semibold text-primary underline">
              Wills, probate and estates — full service page
            </Link>
          </li>
          <li>
            <Link to="/wills-estate-lawyers-sunshine-coast" className="font-semibold text-primary underline">
              Wills and estate lawyers Sunshine Coast
            </Link>
          </li>
          {probateHub ? (
            <li>
              <Link
                to="/probate/$suburb"
                params={{ suburb: probateHub.slug }}
                className="font-semibold text-primary underline"
              >
                Probate solicitors — {probateHub.name}
              </Link>
            </li>
          ) : (
            <li>
              <Link to="/probate" className="font-semibold text-primary underline">
                Fixed-fee probate and estate administration
              </Link>
            </li>
          )}
          <li>
            <Link to="/estate-disputes" className="font-semibold text-primary underline">
              Estate disputes and family provision claims
            </Link>
          </li>
          <li>
            <Link to="/will-safe-custody" className="font-semibold text-primary underline">
              Free safe custody of your original will
            </Link>
          </li>
          <li>
            <Link
              to="/guides/$slug"
              params={{ slug: "do-i-need-probate-in-queensland" }}
              className="font-semibold text-primary underline"
            >
              Do I need probate in Queensland?
            </Link>
          </li>
        </ul>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Other areas we act in</h2>
        <ul className="mt-5 flex flex-wrap gap-2">
          {WILLS_LOCATIONS.filter((l) => l.slug !== content.slug).map((l) => (
            <li key={l.slug}>
              <Link
                to="/wills/$suburb"
                params={{ suburb: l.slug }}
                className="inline-block rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:border-primary"
              >
                Wills {l.name}
              </Link>
            </li>
          ))}
          <li>
            <Link
              to="/scarborough-wills-and-estates"
              className="inline-block rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:border-primary"
            >
              Wills Scarborough
            </Link>
          </li>
        </ul>

        <h2 className="mt-14 font-serif text-3xl text-foreground">
          {content.name} wills and estates questions
        </h2>
        <div className="mt-6">
          <FaqList items={content.faqs} />
        </div>

        <div className="mt-14">
          <EnquiryForm heading={`Book a will appointment in ${content.name}`} suburb={content.name} />
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5">
        <ReviewStrip suburb={content.name} />
      </section>

      <CtaBand
        title="A will takes less time than you think"
        body={`Call ${FIRM.phone} and we will book a time — at our Golden Beach office, or at your home if that is easier.`}
      />
      <div className="h-20" />
    </>
  );
}
